import { type FC, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { _isEmpty, _isNotEmpty } from '@/littledash';
import type { MetadataField } from '@/model/Metadata.model';
import type { SpeciesV1 } from '@/model/Species.model';
import { DateUtils } from '@/utils/Date.utils';
import { useEntitySchemaFieldsByType } from '@/utils/entitySchema/useEntitySchema';
import { useSpeciesStrain } from '@/components/Animals/SpeciesStrain/useSpeciesStrain';
import DatePickerNative from '@/components/UI/DatePickerNative';
import { metadataFieldBuilder } from '@/components/UI/FormFields/FormField.utils';
import FormRender from '@/components/UI/FormRender/FormRender';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import type { AnimalSex } from '@/model/Animal.model';
import { getSexOptions } from '@/model/Animal.utils';
import { featuresSelector } from '@/support/Selectors';

const unknownSexSelector = createSelector([featuresSelector], (features) => ({
  unknownSexEnabled: features?.unknown_sex === true,
}));

interface SpeciesFormProps {
  selectableSpecies: Array<SpeciesV1>;
  fieldPath: string;
  speciesRequired: boolean;
}

const SpeciesForm: FC<SpeciesFormProps> = ({ selectableSpecies, fieldPath, speciesRequired }) => {
  const { control, getValues, trigger } = useFormContext();
  const { speciesFieldName, strainFieldName, strains, hasSelectableSpecies } = useSpeciesStrain({
    selectableSpecies,
    fieldPath,
  });

  if (!hasSelectableSpecies) {
    return null;
  }

  return (
    <div>
      <div className="flex items-top">
        <Controller
          name={speciesFieldName}
          control={control}
          rules={{
            validate: (value) => {
              if (speciesRequired && _isEmpty(value)) {
                return 'Species is required';
              }
              return (
                !(_isNotEmpty(value) && _isEmpty(getValues(strainFieldName))) ||
                'Strain is required when Species is selected'
              );
            },
          }}
          render={({ field: { value, onChange, onBlur } }) => (
            <div className="w-50 ph2">
              <label htmlFor={speciesFieldName}>Species</label>
              <select
                id={speciesFieldName}
                style={{ marginBottom: 0 }}
                value={value ?? ''}
                onBlur={onBlur}
                onChange={onChange}
              >
                <option value="">Choose a species</option>
                {selectableSpecies.map(({ id, name }) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          )}
        />
        <Controller
          name={strainFieldName}
          control={control}
          rules={{
            validate: () => {
              trigger(speciesFieldName);
              return true;
            },
          }}
          render={({ field: { value, onChange, onBlur } }) => (
            <div className="w-50 ph2">
              <label htmlFor={strainFieldName}>Strain</label>
              <select
                id={strainFieldName}
                style={{ marginBottom: 0 }}
                value={value ?? ''}
                disabled={_isEmpty(strains)}
                onChange={onChange}
                onBlur={onBlur}
              >
                <option value="">Choose a strain</option>
                {strains.map(({ id, name, api_id }) => (
                  <option key={id} value={api_id}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          )}
        />
      </div>
    </div>
  );
};

interface AnimalFormFieldsProps {
  index: number;
  name: string;
  cage_id: number;
  sex: string;
  dob: string | null;
  selectableSpecies?: Array<SpeciesV1>;
  requiredMetadata?: Array<MetadataField>;
}

const AnimalFormFields: FC<AnimalFormFieldsProps> = ({
  index,
  name,
  sex,
  dob,
  selectableSpecies = [],
  requiredMetadata = [],
}) => {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext();
  const entityFieldRequiredMap = useEntitySchemaFieldsByType('animal');
  const isDobRequired = entityFieldRequiredMap?.dob ?? true;
  const isSpeciesRequired = entityFieldRequiredMap?.species ?? false;
  const { unknownSexEnabled } = useSelector(unknownSexSelector);
  const sexOptions = getSexOptions(unknownSexEnabled);

  const todayInUserTimezone = DateUtils.formatISODateTimeToDate(DateUtils.dateTimeNow());

  const fieldPath = `animals.${index}`;

  const metadataFields = useMemo(
    () =>
      requiredMetadata.map((field) => ({
        title: field.title,
        // Blank label so FormField omits its ' (required)' suffix; it is rendered alongside the field instead.
        descriptor: { ...metadataFieldBuilder(field), name: `${fieldPath}.meta.${field.slug}`, label: '' },
      })),
    [requiredMetadata, fieldPath]
  );

  return (
    <div className="bg-light-silver ph2 pv3 br2">
      <input type="hidden" {...register(`${fieldPath}.cage_id`)} />
      <div className="mb3 ph2">
        <label htmlFor={`${fieldPath}.name`}>Name</label>
        <input
          type="text"
          id={`${fieldPath}.name`}
          defaultValue={name}
          {...register(`${fieldPath}.name`, { required: 'Name field is required' })}
          style={{ marginBottom: 0 }}
        />
        <ErrorMessage
          errors={errors}
          name={`${fieldPath}.name`}
          render={({ message }) => <p className="f6 red db pt2">{message}</p>}
        />
      </div>
      <div className="mb3 flex items-start">
        <div className="w-50 ph2">
          <label>Sex</label>
          <Controller
            name={`${fieldPath}.sex`}
            defaultValue={sex}
            control={control}
            rules={{
              validate: (value: string) => {
                if (!unknownSexEnabled && value === 'u') {
                  return 'Unknown sex is not available';
                }
                return true;
              },
            }}
            render={({ field: { onChange, value } }) => (
              <select
                id={`${fieldPath}.sex`}
                value={value}
                onChange={(e) => onChange(e.target.value as AnimalSex)}
                data-testid={`animal-${index}-sex-select`}
                style={{ marginBottom: 0 }}
              >
                {sexOptions.map(({ label, value: optValue }) => (
                  <option key={optValue} value={optValue}>
                    {label}
                  </option>
                ))}
              </select>
            )}
          />
        </div>
        <div className="w-50 ph2">
          <label htmlFor={`${fieldPath}.dob`}>D.O.B</label>
          <Controller
            name={`${fieldPath}.dob`}
            control={control}
            rules={{
              validate: (value) => {
                if (_isEmpty(value)) {
                  return !isDobRequired || 'DOB is required';
                }
                return value <= todayInUserTimezone || 'DOB cannot be in the future';
              },
            }}
            defaultValue={isDobRequired ? dob : ''}
            render={({ field: { onChange, value } }) => (
              <DatePickerNative
                value={value}
                max={todayInUserTimezone}
                style={{ marginBottom: 0 }}
                onChange={onChange}
              />
            )}
          />
          <ErrorMessage
            errors={errors}
            name={`${fieldPath}.dob`}
            render={({ message }) => <p className="f6 red db pt2">{message}</p>}
          />
        </div>
      </div>
      <SpeciesForm selectableSpecies={selectableSpecies} fieldPath={fieldPath} speciesRequired={isSpeciesRequired} />
      <ErrorMessage
        errors={errors}
        name={`${fieldPath}.species_id`}
        render={({ message }) => <p className="f6 red db pt2 ph2">{message}</p>}
      />
      {_isNotEmpty(metadataFields) && (
        <div className="ph2 pt3" data-test-element="required-metadata">
          {metadataFields.map(({ title, descriptor }) => (
            <div key={descriptor.name}>
              <label htmlFor={descriptor.name}>{title}</label>
              <FormRender fields={[descriptor]} testPrefix={`animal-${index}-metadata`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnimalFormFields;
